import { filter } from '@atlaskit/adf-utils/traverse';

import type {
	PreloadFailureReason,
	PreloadFailureResult,
} from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types';
import { supportedExtensions } from '@confluence/fabric-extension-lib/entry-points/extensionConstants';
import { MacrosQuery, getMultiMacroQueryBlocklistFF } from '@confluence/fabric-extension-queries';
import { query } from '@confluence/query-preloader-tools';

import { DEFAULT_PRELOAD_COUNT } from './preloader-constants';
import { macroPreloadGuard } from './macroPreloadGuard';

const extractExtensions = (adf: any) => filter(adf, (node) => supportedExtensions.has(node.type));

const hasExtensions = (contentNodes: any) => {
	try {
		const adf = JSON.parse(contentNodes[0].body.dynamic.value);
		const extensions = extractExtensions(adf);
		return extensions.length > 0;
	} catch (e) {
		return false;
	}
};

const failureIfNotSuccess = (reason: PreloadFailureReason) => (success: boolean) =>
	success ? undefined : { MacrosQuery_extensions: reason };

const preloadMacrosSSRAndReturnTopXFailed = async (
	contentId: string,
	contentNodesOrPromise?: any,
) => {
	const blocklist = getMultiMacroQueryBlocklistFF();

	const isContentNodePromise = typeof contentNodesOrPromise?.then === 'function';
	if (contentNodesOrPromise && !isContentNodePromise) {
		if (!hasExtensions(contentNodesOrPromise)) {
			return {};
		}
	}

	const noExtensionsSignal = new Promise<void>((resolve) => {
		if (isContentNodePromise)
			contentNodesOrPromise.then((contentNodes: any) => {
				if (!hasExtensions(contentNodes)) {
					resolve();
				}
			});
	});

	const preloadResult = await macroPreloadGuard(
		'MacrosQuery_preload',
		() => {
			return query({
				variables: {
					first: DEFAULT_PRELOAD_COUNT,
					contentId,
					blocklist,
				},
				query: MacrosQuery,
				context: { single: true },
			});
		},
		noExtensionsSignal,
	).then(failureIfNotSuccess('timeout-rendered-macros'));

	return preloadResult ? Object.assign({}, ...[preloadResult]) : {};
};

export async function preloadMacrosSSRMultiple(
	contentId: string,
	contentNodesOrPromise?: any,
): Promise<PreloadFailureResult | undefined> {
	// Function has early return so we extracted it out to avoid setting global in every return
	const topXFailed = await preloadMacrosSSRAndReturnTopXFailed(contentId, contentNodesOrPromise);
	window['__SSR_MACRO_PRELOAD_TOPX_FAILS__'] = topXFailed;

	return topXFailed;
}
